import React, { useEffect, useState } from 'react';

import { Helmet } from 'react-helmet';
import auth from '~/auth';
import { isAuthV2Enabled } from '~/auth/provider';

import { Alert, Box, Container, Link, Typography } from '@mui/material';

import useAppNavigation from '~/hooks/useAppNavigation';

import ExpiredTokenMessage from './Auth/Messages/ExpiredTokenMessage';
import SocialGoogleButton from './Auth/SocialLogin/SocialGoogleButton';
import SsoSignInButton from './Auth/SocialLogin/SsoSignInButton';

function LoginPage() {
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [showExpiredTokenMessage, setShowExpiredTokenMessage] = useState(false);
  const [isProcessingCredential, setIsProcessingCredential] = useState(false);
  const [showLegacySignIn, setShowLegacySignIn] = useState(false);
  const { push } = useAppNavigation();
  const isAuthV2LoginEnabled = isAuthV2Enabled();
  const isGoogleLoginEnabled = window.configs.GOOGLE_LOGIN_ENABLED === 'true';
  const canRenderGoogleLogin =
    isGoogleLoginEnabled && Boolean(window.google || window.configs.GOOGLE_AUTH_REDIRECT_DOMAIN);
  const showSso = isAuthV2LoginEnabled;
  const showGoogle = canRenderGoogleLogin && (!isAuthV2LoginEnabled || showLegacySignIn);
  const showLegacySignInLink = isAuthV2LoginEnabled && canRenderGoogleLogin && !showLegacySignIn;

  useEffect(() => {
    setShowExpiredTokenMessage(auth.consumeNotice()?.type === 'session-expired');
  }, []);

  // Handle credentials from redirect flows.
  useEffect(() => {
    const windowParams = new URLSearchParams(window.location.search);
    const hasLegacyCredential = !!windowParams.get('credential');
    const hasOidcCallback = !!windowParams.get('state') && (!!windowParams.get('code') || !!windowParams.get('error'));

    if (!hasLegacyCredential && !hasOidcCallback) {
      return;
    }

    setIsProcessingCredential(true);

    void auth
      .completeLoginIfNeeded()
      .then(() => {
        push('/');
      })
      .catch((err) => {
        setIsProcessingCredential(false);
        setErrorMessage(err instanceof Error ? err.message : 'Sign in failed');
      });
  }, [push]);

  // Show a loading state while processing a redirect callback.
  if (isProcessingCredential) {
    return (
      <Container>
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography>Signing in...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      {showExpiredTokenMessage && <ExpiredTokenMessage />}
      <Helmet>
        <title>Login</title>
      </Helmet>

      <Box className="row flex-centered">
        <Box sx={{ mt: 6 }}>
          <Typography variant="h1" className="page-header">
            Login
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 360, marginTop: 2 }}>
            <Typography>Sign in to access the admin portal.</Typography>

            {errorMessage && <Alert severity="warning">{errorMessage}</Alert>}

            {showSso && <SsoSignInButton onError={setErrorMessage} />}

            {showLegacySignInLink && (
              <Typography variant="body2">
                <Link component="button" type="button" onClick={() => setShowLegacySignIn(true)}>
                  Use legacy sign-in
                </Link>
              </Typography>
            )}

            {showLegacySignIn && (
              <Alert severity="info">
                Legacy sign-in will soon be removed. Only use it if there is an unexpected issue with the new login, and
                inform the IT support team about the issue.
              </Alert>
            )}

            {showGoogle && <SocialGoogleButton onError={setErrorMessage} />}
          </Box>
        </Box>
      </Box>
    </Container>
  );
}

export default LoginPage;
